import { Ionicons } from '@expo/vector-icons';
import React from 'react';
import { StyleSheet, View } from 'react-native';
import Svg, { Circle, G } from 'react-native-svg';
import { SizableText } from 'tamagui';

interface CircularProgressIconProps {
  progress: number; // 0-100
  size?: number; // Size of the circle
  strokeWidth?: number; // Width of the progress stroke
  showPercentage?: boolean; // Show percentage text below
  showPlayIcon?: boolean; // Show play icon in center
  iconColor?: string;
  progressColor?: string;
  backgroundColor?: string;
}

export const CircularProgressIcon: React.FC<CircularProgressIconProps> = ({
  progress,
  size = 40,
  strokeWidth = 3,
  showPercentage = false,
  showPlayIcon = true,
  iconColor = '#1B1B1B', // Dark gray/black play icon
  progressColor = '#F15A29', // Orange progress segment
  backgroundColor = '#1B1B1B', // Dark gray/black circular ring background
}) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (progress / 100) * circumference;
  const center = size / 2;

  return (
    <View style={[styles.container, { width: size, height: size + (showPercentage ? 20 : 0) }]}>
      <View style={[styles.circleContainer, { width: size, height: size }]}>
        {/* SVG Circle for progress */}
        <Svg width={size} height={size} style={styles.svg}>
          {/* Background circle */}
          <Circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke={backgroundColor}
            strokeWidth={strokeWidth}
          />
        {/* Progress circle - rotated to start from top */}
        <G transform={`translate(${center}, ${center}) rotate(-90) translate(-${center}, -${center})`}>
          <Circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke={progressColor}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
          />
        </G>
        </Svg>
        
        {/* Play icon in center */}
        {showPlayIcon && (
          <View style={styles.iconContainer}>
            <Ionicons name="play" size={size * 0.5} color={iconColor} />
          </View>
        )}
      </View>
      
      {/* Percentage text (optional, below icon) */}
      {showPercentage && (
        <View style={styles.percentageContainer}>
          <SizableText size="$1" fontWeight="600" color={iconColor}>
            {Math.round(progress)}%
          </SizableText>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  circleContainer: {
    position: 'relative',
    justifyContent: 'center',
    alignItems: 'center',
  },
  svg: {
    position: 'absolute',
    top: 0,
    left: 0,
  },
  iconContainer: {
    position: 'absolute',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1,
  },
  percentageContainer: {
    marginTop: 4,
    justifyContent: 'center',
    alignItems: 'center',
  },
});

